/**
 * AdminPageHeader — the P0 kit's canonical page scaffold (spec §3 Phase 0).
 * Renders exactly ONE `<h1>` with the shared admin title treatment, an optional
 * subtitle, and a right-aligned actions slot. Every admin page (P1a/P1b/P2/P3)
 * uses this for its title row instead of a bespoke `<h1>`, so the heading
 * hierarchy + visual language stay uniform across the wave. Purely
 * presentational — the caller passes already-translated strings/nodes.
 * `--g-*` tokens only. Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement, type ReactNode } from "react";

export interface AdminPageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  testid?: string;
}

export function AdminPageHeader({
  title,
  subtitle,
  actions,
  testid = "admin-page-header",
}: AdminPageHeaderProps): ReactElement {
  return (
    <header
      data-testid={testid}
      className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-[var(--g-primary-ink)]">{title}</h1>
        {subtitle ? (
          <p data-testid={`${testid}-subtitle`} className="text-sm text-[var(--g-secondary)]">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div data-testid={`${testid}-actions`} className="flex shrink-0 items-center gap-2">
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export default AdminPageHeader;
